import type { ProcessImageParameters } from '@ai-color-modules/gen_proto/ai_color_simulation/v1/messages_pb'
import type { ColorOption } from '@ai-color-modules/simulation/ColorPalette/colorOptions'
import type { MaterialOption } from '@ai-color-modules/simulation/MaterialPalette/materialOptions'

import { rgbToHex } from './colorOptionFallback'
import { API_TO_AREA_NAME_MAP, BASE_AREAS } from './processParameters'

export interface SummaryRow {
  /** 部位名（"屋根" / "カスタム1" 等） */
  label: string
  /** 色見本を出すか素材見本を出すか */
  kind: 'color' | 'material'
  /** kind=='color' の時のみセットされる。色見本に使う hex */
  hex?: string
  /** kind=='material' の時にセットされる。素材見本に使うサムネイル URL（無ければ枠のみ） */
  thumbnailUrl?: string
  /** 表示テキスト（色名 or 素材名） */
  text: string
}

export interface BuildSummaryRowsInput {
  parameters: ProcessImageParameters
  availableColors: ColorOption[]
  availableMaterials: MaterialOption[]
}

/**
 * カラーコード（例: "N-35"）から表示用の色行を組み立てる.
 * - hex: availableColors にあれば colorOption.color、無ければ proto の r/g/b から生成
 * - text: availableColors にあれば colorOption.label、無ければカラーコード自体
 */
const buildColorRow = (
  label: string,
  colorCode: string,
  r: number,
  g: number,
  b: number,
  availableColors: ColorOption[],
): SummaryRow => {
  const option = availableColors.find(c => c.value === colorCode)
  return {
    label,
    kind: 'color',
    hex: option?.color ?? rgbToHex(r, g, b),
    text: option?.label ?? colorCode,
  }
}

/** 素材行の表示テキスト（ブランド/品番/色名）とサムネイル URL を組み立てる */
const buildMaterialRow = (
  label: string,
  materialId: bigint,
  availableMaterials: MaterialOption[],
): SummaryRow => {
  const material = availableMaterials.find(m => m.id === materialId)
  const fallback = `素材 #${materialId.toString()}`
  if (!material) {
    return { label, kind: 'material', text: fallback }
  }
  return {
    label,
    kind: 'material',
    // 表示は「品番 / カラバリ（色名）」。どちらか欠ければあるものだけ、両方無ければフォールバック
    text: [material.productCode, material.colorName].filter(Boolean).join(' / ') || fallback,
    thumbnailUrl: material.thumbnailUrl || undefined,
  }
}

/**
 * 表示中の結果画像 (selectedProcessedImage.parameters) からサマリ行を構築する。
 * 編集中フォーム state は参照しない（仕様）。
 *
 * - BASE_AREAS の順に走査
 *   - mainAreaMaterials に有効な materialId があれば素材行（色見本なし）
 *   - そうでなく mainAreas に有効な color hex があれば色行
 *   - どちらも無ければスキップ（=その部位は色未適用）
 * - subAreas は color hex があるものだけ。label 未指定は「カスタム{idx+1}」にフォールバック
 */
export const buildSummaryRows = ({
  parameters,
  availableColors,
  availableMaterials,
}: BuildSummaryRowsInput): SummaryRow[] => {
  const rows: SummaryRow[] = []

  const mainAreas = parameters.mainAreas ?? []
  const mainAreaMaterials = parameters.mainAreaMaterials ?? []

  for (const area of BASE_AREAS) {
    const material = mainAreaMaterials.find(
      m => API_TO_AREA_NAME_MAP[m.areaName] === area && m.materialId !== 0n,
    )
    if (material) {
      rows.push(buildMaterialRow(area, material.materialId, availableMaterials))
      continue
    }

    const main = mainAreas.find(m => API_TO_AREA_NAME_MAP[m.areaName] === area && m.color !== '')
    if (main) {
      rows.push(buildColorRow(area, main.color, main.r, main.g, main.b, availableColors))
    }
  }

  const subAreas = parameters.subAreas ?? []
  subAreas.forEach((subArea, index) => {
    if (subArea.color === '') return
    rows.push(
      buildColorRow(
        subArea.label || `カスタム${index + 1}`,
        subArea.color,
        subArea.r,
        subArea.g,
        subArea.b,
        availableColors,
      ),
    )
  })

  return rows
}
